import { supabase } from '../lib/supabase';

// Admin User Management Functions
export const getAllUsers = async (filters = {}) => {
  try {
    let query = supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false });

    if (filters?.role) {
      query = query?.eq('role', filters?.role);
    }

    if (filters?.is_active !== undefined) {
      query = query?.eq('is_active', filters?.is_active);
    }

    if (filters?.search) {
      query = query?.or(`full_name.ilike.%${filters?.search}%,email.ilike.%${filters?.search}%`);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data;
  } catch (error) {
    throw error;
  }
};

export const createAdminUser = async (userData) => {
  try {
    const { email, password, full_name, role, phone = null } = userData;
    
    // Create auth user first
    const { data: authData, error: authError } = await supabase?.auth?.signUp({
      email,
      password,
    });

    if (authError) throw authError;

    if (authData?.user) {
      // Create user profile
      const { data, error } = await supabase?.from('user_profiles')?.insert([
        {
          id: authData?.user?.id,
          email,
          full_name,
          role,
          phone,
          is_active: true,
        }
      ])?.select()?.single();

      if (error) throw error;
      return { user: authData?.user, profile: data };
    }
  } catch (error) {
    throw error;
  }
};

export const updateUserRole = async (userId, role) => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.update({ role })?.eq('id', userId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    throw error;
  }
};

export const toggleUserActiveStatus = async (userId, is_active) => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.update({ is_active })?.eq('id', userId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    throw error;
  }
};

export const deleteUserAccount = async (userId) => {
  try {
    const { error } = await supabase?.from('user_profiles')?.delete()?.eq('id', userId);

    if (error) throw error;
    return true;
  } catch (error) {
    throw error;
  }
};

// Admin Event Management Functions
export const getAllEvents = async (filters = {}) => {
  try {
    let query = supabase?.from('events')?.select(`
      *,
      venue:venues(*),
      organizer:user_profiles(id, full_name, email),
      category:event_categories(*)
    `)?.order('created_at', { ascending: false });

    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }

    if (filters?.is_featured !== undefined) {
      query = query?.eq('is_featured', filters?.is_featured);
    }

    if (filters?.search) {
      query = query?.or(`title.ilike.%${filters?.search}%,description.ilike.%${filters?.search}%`);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data;
  } catch (error) {
    throw error;
  }
};

export const updateEventStatus = async (eventId, status) => {
  try {
    const { data, error } = await supabase?.from('events')?.update({ status })?.eq('id', eventId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    throw error;
  }
};

export const updateEventFeatured = async (eventId, is_featured) => {
  try {
    const { data, error } = await supabase?.from('events')?.update({ is_featured })?.eq('id', eventId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    throw error;
  }
};

export const deleteEvent = async (eventId) => {
  try {
    const { error } = await supabase?.from('events')?.delete()?.eq('id', eventId);

    if (error) throw error;
    return true;
  } catch (error) {
    throw error;
  }
};

// Admin Event Advanced Analytics Functions
export const getRecentEvents = async (limit = 10) => {
  try {
    const { data, error } = await supabase?.from('events')
      ?.select(`
        *,
        venue:venues(name, city),
        organizer:user_profiles(full_name)
      `)
      ?.order('created_at', { ascending: false })
      ?.limit(limit);

    if (error) throw error;
    return data;
  } catch (error) {
    throw error;
  }
};

export const getTopVenuesBySales = async (limit = 10) => {
  try {
    const { data, error } = await supabase?.from('venues')
      ?.select(`
        *,
        events!inner(
          id,
          orders!inner(
            total_amount,
            status
          )
        )
      `)
      ?.eq('events.orders.status', 'confirmed');

    if (error) throw error;

    // Calculate sales totals per venue
    const venueStats = data?.reduce((acc, venue) => {
      const totalSales = venue?.events?.reduce((eventSum, event) => {
        const eventSales = event?.orders?.reduce((orderSum, order) => {
          return orderSum + parseFloat(order?.total_amount || 0);
        }, 0);
        return eventSum + eventSales;
      }, 0);

      if (totalSales > 0) {
        acc?.push({
          ...venue,
          total_sales: totalSales,
          event_count: venue?.events?.length || 0
        });
      }
      return acc;
    }, []);

    // Sort by sales and limit
    return venueStats
      ?.sort((a, b) => b?.total_sales - a?.total_sales)
      ?.slice(0, limit);
  } catch (error) {
    throw error;
  }
};

export const getTopMerchandiseAccounts = async (limit = 10) => {
  try {
    // Get merchandise sales data with user profiles
    const { data, error } = await supabase?.from('merchandise')
      ?.select(`
        *,
        event:events(
          id,
          title,
          organizer_id,
          organizer:user_profiles(full_name, email)
        )
      `);

    if (error) throw error;

    // Group by organizer and calculate performance
    const merchandiseStats = data?.reduce((acc, merch) => {
      const organizerId = merch?.event?.organizer_id;
      const organizerName = merch?.event?.organizer?.full_name;
      const organizerEmail = merch?.event?.organizer?.email;

      if (organizerId && !acc?.[organizerId]) {
        acc[organizerId] = {
          organizer_id: organizerId,
          organizer_name: organizerName,
          organizer_email: organizerEmail,
          total_merchandise_items: 0,
          total_potential_value: 0,
          active_items: 0,
          events_count: new Set()
        };
      }

      if (organizerId) {
        acc[organizerId].total_merchandise_items += 1;
        acc[organizerId].total_potential_value += parseFloat(merch?.price || 0) * (merch?.stock_quantity || 0);
        if (merch?.status === 'active') {
          acc[organizerId].active_items += 1;
        }
        acc?.[organizerId]?.events_count?.add(merch?.event?.id);
      }

      return acc;
    }, {});

    // Convert to array and calculate final stats
    const accountsArray = Object.values(merchandiseStats)?.map(account => ({
      ...account,
      events_count: account?.events_count?.size || 0,
      performance_score: (account?.active_items * 2) + account?.events_count + (account?.total_potential_value / 10000)
    }));

    // Sort by performance score and limit
    return accountsArray
      ?.sort((a, b) => b?.performance_score - a?.performance_score)
      ?.slice(0, limit);
  } catch (error) {
    throw error;
  }
};

export const searchEventsAdvanced = async (query) => {
  try {
    if (!query?.trim()) return [];

    const { data, error } = await supabase?.from('events')
      ?.select(`
        *,
        venue:venues(name, city, address_line_1),
        organizer:user_profiles(full_name),
        category:event_categories(name)
      `)
      ?.or(`
        title.ilike.%${query}%,
        description.ilike.%${query}%,
        short_description.ilike.%${query}%,
        venue.name.ilike.%${query}%,
        venue.city.ilike.%${query}%,
        venue.address_line_1.ilike.%${query}%
      `);

    if (error) throw error;

    // Additional filtering for date-based searches
    const dateQuery = query?.toLowerCase();
    let dateFilteredData = data;

    if (dateQuery?.includes('today') || dateQuery?.includes('tomorrow') || dateQuery?.includes('week') || dateQuery?.includes('month')) {
      const now = new Date();
      let filterDate;

      if (dateQuery?.includes('today')) {
        filterDate = now?.toISOString()?.split('T')?.[0];
      } else if (dateQuery?.includes('tomorrow')) {
        const tomorrow = new Date(now);
        tomorrow?.setDate(now?.getDate() + 1);
        filterDate = tomorrow?.toISOString()?.split('T')?.[0];
      } else if (dateQuery?.includes('week')) {
        const weekFromNow = new Date(now);
        weekFromNow?.setDate(now?.getDate() + 7);
        dateFilteredData = data?.filter(event => {
          const eventDate = new Date(event?.event_date);
          return eventDate >= now && eventDate <= weekFromNow;
        });
      } else if (dateQuery?.includes('month')) {
        const monthFromNow = new Date(now);
        monthFromNow?.setMonth(now?.getMonth() + 1);
        dateFilteredData = data?.filter(event => {
          const eventDate = new Date(event?.event_date);
          return eventDate >= now && eventDate <= monthFromNow;
        });
      }

      if (filterDate) {
        dateFilteredData = data?.filter(event => 
          event?.event_date?.startsWith(filterDate)
        );
      }
    }

    return dateFilteredData;
  } catch (error) {
    throw error;
  }
};

// Admin Financial Functions
export const getFinancialSummary = async (dateRange = {}) => {
  try {
    let query = supabase?.from('orders')?.select(`
      *,
      event:events(title),
      user:user_profiles(full_name, email),
      payment_transactions(*)
    `);

    if (dateRange?.start_date) {
      query = query?.gte('created_at', dateRange?.start_date);
    }

    if (dateRange?.end_date) {
      query = query?.lte('created_at', dateRange?.end_date);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data;
  } catch (error) {
    throw error;
  }
};

export const getRevenueStats = async (period = 'month') => {
  try {
    const { data, error } = await supabase?.from('platform_metrics')?.select('*')?.eq('metric_name', `revenue_${period}`)?.order('metric_date', { ascending: false })?.limit(12);

    if (error) throw error;
    return data;
  } catch (error) {
    throw error;
  }
};

export const exportOrdersCSV = async (filters = {}) => {
  try {
    let query = supabase?.from('orders')?.select(`
      order_number,
      customer_email,
      total_amount,
      status,
      payment_status,
      created_at,
      event:events(title),
      user:user_profiles(full_name)
    `);

    if (filters?.start_date) {
      query = query?.gte('created_at', filters?.start_date);
    }

    if (filters?.end_date) {
      query = query?.lte('created_at', filters?.end_date);
    }

    const { data, error } = await query;

    if (error) throw error;
    
    // Convert to CSV format
    const headers = ['Order Number', 'Customer Email', 'Customer Name', 'Event', 'Amount', 'Status', 'Payment Status', 'Date'];
    const csvData = data?.map(order => [
      order?.order_number,
      order?.customer_email,
      order?.user?.full_name || 'N/A',
      order?.event?.title || 'N/A',
      order?.total_amount,
      order?.status,
      order?.payment_status,
      new Date(order?.created_at)?.toLocaleDateString()
    ]);

    return { headers, data: csvData };
  } catch (error) {
    throw error;
  }
};

// Admin Audit Functions
export const getAuditLogs = async (filters = {}) => {
  try {
    let query = supabase?.from('audit_logs')?.select(`
      *,
      user:user_profiles(full_name, email)
    `)?.order('created_at', { ascending: false });

    if (filters?.table_name) {
      query = query?.eq('table_name', filters?.table_name);
    }

    if (filters?.action) {
      query = query?.eq('action', filters?.action);
    }

    if (filters?.user_id) {
      query = query?.eq('user_id', filters?.user_id);
    }

    if (filters?.start_date) {
      query = query?.gte('created_at', filters?.start_date);
    }

    if (filters?.end_date) {
      query = query?.lte('created_at', filters?.end_date);
    }

    const { data, error } = await query?.limit(100);

    if (error) throw error;
    return data;
  } catch (error) {
    throw error;
  }
};

// Admin System Configuration Functions
export const getSystemConfig = async () => {
  try {
    const { data, error } = await supabase?.from('system_configuration')?.select('*')?.order('config_key');

    if (error) throw error;
    return data;
  } catch (error) {
    throw error;
  }
};

export const updateSystemConfig = async (configKey, configValue, description = null) => {
  try {
    const { data, error } = await supabase?.from('system_configuration')?.upsert({
      config_key: configKey,
      config_value: configValue,
      description,
      is_public: false
    })?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    throw error;
  }
};

// Admin Platform Metrics Functions
export const getPlatformMetrics = async (metricName = null) => {
  try {
    let query = supabase?.from('platform_metrics')?.select('*')?.order('metric_date', { ascending: false });

    if (metricName) {
      query = query?.eq('metric_name', metricName);
    }

    const { data, error } = await query?.limit(50);

    if (error) throw error;
    return data;
  } catch (error) {
    throw error;
  }
};

export const getDashboardStats = async () => {
  try {
    // Get various counts for dashboard
    const [usersResult, eventsResult, ordersResult, revenueResult] = await Promise.all([
      supabase?.from('user_profiles')?.select('role', { count: 'exact' }),
      supabase?.from('events')?.select('status', { count: 'exact' }),
      supabase?.from('orders')?.select('status', { count: 'exact' }),
      supabase?.from('orders')?.select('total_amount')?.eq('status', 'confirmed')
    ]);

    const totalRevenue = revenueResult?.data?.reduce((sum, order) => sum + parseFloat(order?.total_amount || 0), 0) || 0;

    return {
      users: {
        total: usersResult?.count || 0,
        admins: usersResult?.data?.filter(u => u?.role === 'admin')?.length || 0,
        managers: usersResult?.data?.filter(u => u?.role === 'manager')?.length || 0,
        consumers: usersResult?.data?.filter(u => u?.role === 'consumer')?.length || 0
      },
      events: {
        total: eventsResult?.count || 0,
        published: eventsResult?.data?.filter(e => e?.status === 'published')?.length || 0,
        draft: eventsResult?.data?.filter(e => e?.status === 'draft')?.length || 0
      },
      orders: {
        total: ordersResult?.count || 0,
        confirmed: ordersResult?.data?.filter(o => o?.status === 'confirmed')?.length || 0,
        pending: ordersResult?.data?.filter(o => o?.status === 'pending')?.length || 0
      },
      revenue: {
        total: totalRevenue
      }
    };
  } catch (error) {
    throw error;
  }
};